import { supabase } from './supabase';
import { updateOwnProfile, type ProfileEditPatch } from './queries';

/**
 * Onboarding-specific query helpers.
 *
 * Kept separate from queries.ts per task scope. Profile writes during
 * onboarding reuse updateOwnProfile from queries.ts — we don't duplicate
 * that logic here.
 */

/**
 * Check whether a handle is free (or belongs to the current user, which is
 * also fine — lets a user re-save the same handle without a false conflict).
 *
 * Returns true if the handle is available, false if taken by someone else.
 */
export async function checkHandleAvailable(handle: string, signal?: AbortSignal): Promise<boolean> {
  const normalized = handle.trim().toLowerCase();
  if (!normalized) return false;

  const { data: sessionData } = await supabase.auth.getSession();
  const currentUserId = sessionData.session?.user.id ?? null;

  let q = supabase
    .from('profiles')
    .select('id')
    .eq('handle', normalized)
    .limit(1);
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q.maybeSingle();

  if (error) throw error;
  if (!data) return true;
  return data.id === currentUserId;
}

/**
 * Final commit of the onboarding flow — saves the full profile patch.
 * Thin wrapper around updateOwnProfile so the wizard has a single,
 * intention-revealing call site for completion.
 */
export async function markOnboardingComplete(
  userId: string,
  patch: ProfileEditPatch,
): Promise<void> {
  await updateOwnProfile(userId, patch);
}
